import apiBase from "@/api/unversioned/apiBase";
import { type AxiosRequestConfig } from "axios";
import type { CreateTemplatePayload, GetTemplatesParams } from "@/features/reporting/interfaces/report";
import { type SystemTemplatesResponse } from "@/features/reporting/interfaces/TemplateInterfaces";
import { logError, FEATURE_NAMES } from "@/loggers";
import { Capability } from "@/global";

const baseURL = `${import.meta.env.VITE_API_BASE_URL}/reporting-bff`;

const getResults = async (config?: AxiosRequestConfig) => {
  return apiBase("get", `${baseURL}/reports`, config);
};

const deleteResult = async (reportId: string, config?: AxiosRequestConfig) => {
  return apiBase("delete", `${baseURL}/reports/${reportId}`, config);
};

const getTemplates = async (params?: GetTemplatesParams, config?: AxiosRequestConfig) => {
  return apiBase("get", `${baseURL}/templates`, { ...config, params });
};

const getTemplateById = async (templateId: string, config?: AxiosRequestConfig) => {
  return apiBase("get", `${baseURL}/templates/${templateId}`, config);
};

const createTemplate = async (payload: CreateTemplatePayload, config?: AxiosRequestConfig) => {
  return apiBase("post", `${baseURL}/templates`, config, payload);
};

const updateTemplate = async (templateId: string, payload: CreateTemplatePayload, config?: AxiosRequestConfig) => {
  return apiBase("put", `${baseURL}/templates/${templateId}`, config, payload);
};

const deleteTemplate = async (templateId: string, config?: AxiosRequestConfig) => {
  return apiBase("delete", `${baseURL}/templates/${templateId}`, config);
};

const runTemplate = async (templateId: string, config?: AxiosRequestConfig) => {
  return apiBase("post", `${baseURL}/templates/${templateId}/run`, config);
};

const getSystemTemplates = async (capability: Capability, config?: AxiosRequestConfig) => {
  try {
    const response = await apiBase<SystemTemplatesResponse>("get", `${baseURL}/templates/system`, {
      ...config,
      params: {
        capability: capability,
        ...config?.params
      }
    });

    return response;
  } catch (error) {
    logError(FEATURE_NAMES.MVS_REPORTING, `Unexpected error in getSystemTemplates: ${error}`);
    throw error;
  }
};

export default {
  // Result methods
  getResults,
  deleteResult,

  // Template methods
  getTemplates,
  getTemplateById,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  runTemplate,
  getSystemTemplates
};
